import { Link } from "react-router-dom";
import { Instagram, Youtube, Linkedin, Music2, MapPin, MessageCircle, Mail } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { brand, contact, socials, channels } from "@kanata/shared";
import { Logo } from "@/components/ui/Logo";

const socialLinks: { icon: LucideIcon; href: string; label: string }[] = [
  { icon: Instagram, href: socials.instagram, label: "Instagram" },
  { icon: Music2, href: socials.tiktok, label: "TikTok" },
  { icon: Youtube, href: socials.youtube, label: "YouTube" },
  { icon: Linkedin, href: socials.linkedin, label: "LinkedIn" },
];

const exploreLinks = [
  { label: "Pengiriman", to: "/informasi/pengiriman" },
  { label: "Garansi", to: "/informasi/garansi" },
  { label: "Pengembalian & dana", to: "/informasi/pengembalian-dana" },
  { label: "Syarat & ketentuan", to: "/informasi/syarat-ketentuan" },
  { label: "Privasi", to: "/informasi/privasi" },
  { label: "Semua meja", to: "/katalog" },
  { label: "Aksesori", to: "/katalog?jenis=aksesori" },
  { label: "Panduan perakitan", to: "/perakitan" },
  { label: "Untuk kantor", to: "/kantor" },
  { label: "Blog", to: "/blog" },
  { label: "Tentang Kanata", to: "/tentang" },
  { label: "Kontak", to: "/kontak" },
  { label: "Cek pesanan", to: "/cek-pesanan" },
];

// Marketplace links, for customers who would rather buy there. Not the buy
// path — the storefront sells directly.
const marketplaceLinks = [
  { label: "Tokopedia", href: channels.tokopedia },
  { label: "Shopee", href: channels.shopee },
  { label: "TikTok Shop", href: channels.tiktok },
];

export function Footer() {
  return (
    <footer className="bg-brand-darker text-white">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid grid-cols-2 gap-10 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <Logo className="h-7 w-auto text-white" />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/70">
              {brand.tagline} {brand.origin}, dipasang sampai jalan.
            </p>
            <div className="mt-5 flex gap-2">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex size-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <s.icon className="size-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-xs font-semibold tracking-[0.14em] text-white/50 uppercase">Jelajahi</h4>
            <ul className="mt-4 space-y-2.5">
              {exploreLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-white/75 transition-colors hover:text-white">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Shop */}
          <div>
            <h4 className="text-xs font-semibold tracking-[0.14em] text-white/50 uppercase">Marketplace</h4>
            <ul className="mt-4 space-y-2.5">
              {marketplaceLinks.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/75 transition-colors hover:text-white"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-semibold tracking-[0.14em] text-white/50 uppercase">Kontak</h4>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-2.5 text-sm text-white/75">
                <MapPin className="mt-0.5 size-4 shrink-0 text-brand" />
                <span>{contact.showroom}</span>
              </li>
              <li>
                <a
                  href={contact.whatsappUrl}
                  data-wa-placement="footer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-white/75 transition-colors hover:text-white"
                >
                  <MessageCircle className="size-4 shrink-0 text-brand" />
                  {contact.whatsappNumber}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-2.5 text-sm text-white/75 transition-colors hover:text-white"
                >
                  <Mail className="size-4 shrink-0 text-brand" />
                  {contact.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 sm:flex-row">
          <p className="text-xs text-white/50">
            © 2026 {brand.name}. Semua hak dilindungi.
          </p>
          <div className="flex items-center gap-3 text-xs text-white/50">
            <span>{brand.origin} 🇮🇩</span>
            <span aria-hidden className="text-white/25">
              ·
            </span>
            <KugieCredit />
          </div>
        </div>
      </div>
    </footer>
  );
}

const minimalLinks = exploreLinks.filter((l) => l.to.startsWith("/informasi/"));

/**
 * A campaign page's footer: who Kanata is, how to reach it, and the policies
 * an ad's landing page has to show. No marketplace links and no site map: a
 * visitor from an ad who wanders to Tokopedia is a sale nobody can credit.
 */
export function MinimalFooter() {
  return (
    <footer className="bg-brand-darker text-white">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-10 sm:px-8 md:flex-row md:items-center md:justify-between">
        <div className="space-y-3">
          <Logo className="h-6 w-auto text-white" />
          <a
            href={contact.whatsappUrl}
            data-wa-placement="footer"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-white/75 hover:text-white"
          >
            <MessageCircle className="size-4" />
            Tanya lewat WhatsApp
          </a>
        </div>
        <nav aria-label="Informasi" className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/65">
          {minimalLinks.map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-white">
              {l.label}
            </Link>
          ))}
        </nav>
      </div>
      <div className="flex flex-col items-center justify-center gap-2 border-t border-white/10 px-5 py-4 text-xs text-white/45 sm:flex-row sm:gap-3">
        <span>
          © {new Date().getFullYear()} {brand.name}
        </span>
        <span aria-hidden className="hidden text-white/25 sm:inline">
          ·
        </span>
        <KugieCredit />
      </div>
    </footer>
  );
}

/**
 * Credit for Kugie, who builds and runs this storefront. The frost logomark
 * is Kugie's variant for dark surfaces, which both footers are.
 */
function KugieCredit() {
  return (
    <a
      href="https://kugie.app/"
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-1.5 transition-colors hover:text-white"
    >
      Dibuat oleh
      <img
        src="/logo/kugie-logomark-horizontal-frost.svg"
        alt="Kugie"
        width={1127}
        height={309}
        className="h-3 w-auto opacity-75 transition-opacity group-hover:opacity-100"
        draggable={false}
      />
    </a>
  );
}
